'use client';

import Image from 'next/image';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useI18n } from '@/providers/i18n';
import type { IncomingImageInput } from '@/components/nodes/shared/use-incoming-image-inputs';
import { hasMention, renderMentionMirror, detectMentionAnchor, resolveMentionCaret, handleMentionAtomicKeys } from '@/components/nodes/shared/mention-mirror';

type Shot = { duration: number; prompt: string };

type MultiShotEditorProps = {
  value: string;
  totalDuration: number;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  imageInputs?: IncomingImageInput[];
};

const MAX_DUR = 12;
const MIN_DUR = 1;
const MIN_SHOTS = 2;
const MAX_SHOTS = 6;

function clamp(d: number): number {
  return Math.max(MIN_DUR, Math.min(MAX_DUR, d));
}

function serialize(shots: Shot[]): string {
  return shots.map(s => `[${s.duration}] ${s.prompt}`).join('\n');
}

function initShots(prompt: string, total: number): Shot[] {
  const safeTot = Math.max(MIN_SHOTS, total);
  const first = clamp(safeTot - 1);
  const shots: Shot[] = [{ duration: first, prompt }];
  let remain = safeTot - first;
  while (remain > 0) {
    const d = clamp(remain);
    shots.push({ duration: d, prompt: '' });
    remain -= d;
  }
  return shots;
}

function balance(shots: Shot[], total: number): Shot[] {
  let sum = shots.reduce((s, sh) => s + sh.duration, 0);
  for (let i = shots.length - 1; i >= 0 && sum < total; i--) {
    const add = Math.min(MAX_DUR - shots[i].duration, total - sum);
    shots[i].duration += add;
    sum += add;
  }
  for (let i = shots.length - 1; i >= 0 && sum > total; i--) {
    const sub = Math.min(shots[i].duration - MIN_DUR, sum - total);
    shots[i].duration -= sub;
    sum -= sub;
  }
  return shots;
}

function parseShots(text: string, total: number): Shot[] {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  if (lines.length === 0 || (lines.length === 1 && !/^\[\d+]/.test(lines[0]))) {
    return initShots(lines[0] ?? '', total);
  }

  const shots = lines.map(line => {
    const m = line.match(/^\[(\d+)]\s*(.*)/);
    return m
      ? { duration: clamp(parseInt(m[1], 10)), prompt: m[2] }
      : { duration: 0, prompt: line };
  });

  const specified = shots.reduce((s, sh) => s + sh.duration, 0);
  const zeros = shots.filter(sh => sh.duration === 0);
  if (zeros.length > 0) {
    const pool = Math.max(zeros.length, total - specified);
    const base = Math.floor(pool / zeros.length);
    let extra = pool - base * zeros.length;
    for (const sh of zeros) {
      sh.duration = clamp(base + (extra > 0 ? 1 : 0));
      if (extra > 0) extra--;
    }
  }

  if (shots.length < MIN_SHOTS) {
    if (shots[0].duration > MIN_DUR) shots[0].duration--;
    shots.push({ duration: MIN_DUR, prompt: '' });
  }

  return balance(shots, total);
}

export function MultiShotEditor({
  value,
  totalDuration,
  onChange,
  onFocus,
  onBlur,
  disabled,
  imageInputs = [],
}: MultiShotEditorProps) {
  const { t } = useI18n();
  const [shots, setShots] = useState<Shot[]>(() => parseShots(value, totalDuration));
  const selfChange = useRef(false);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionShotIdx, setMentionShotIdx] = useState(-1);
  const [mentionListIdx, setMentionListIdx] = useState(0);
  const mentionAnchorRef = useRef<number | null>(null);
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);

  useEffect(() => {
    if (selfChange.current) { selfChange.current = false; return; }
    setShots(parseShots(value, totalDuration));
  }, [value, totalDuration]);

  const emit = useCallback((next: Shot[]) => {
    selfChange.current = true;
    onChange(serialize(next));
  }, [onChange]);

  const updateShot = useCallback((index: number, partial: Partial<Shot>) => {
    setShots(prev => {
      const next = prev.map((s, i) => i === index ? { ...s, ...partial } : s);
      if (partial.duration !== undefined) {
        const target = index === next.length - 1
          ? (next.length >= 2 ? next.length - 2 : -1)
          : next.length - 1;
        if (target >= 0) {
          const maxForIndex = prev[index].duration + (prev[target].duration - MIN_DUR);
          next[index] = { ...next[index], duration: clamp(Math.min(partial.duration, maxForIndex)) };
          const diff = next.reduce((s, sh) => s + sh.duration, 0) - totalDuration;
          next[target] = { ...next[target], duration: next[target].duration - diff };
        }
      }
      emit(next);
      return next;
    });
  }, [emit, totalDuration]);

  const addShot = useCallback(() => {
    setShots(prev => {
      if (prev.length >= MAX_SHOTS) return prev;
      const next = [...prev];
      let borrowed = false;
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].duration > MIN_DUR) {
          next[i] = { ...next[i], duration: next[i].duration - 1 };
          borrowed = true;
          break;
        }
      }
      if (!borrowed) return prev;
      next.push({ duration: 1, prompt: '' });
      emit(next);
      return next;
    });
  }, [emit]);

  const handleShotPromptChange = useCallback((idx: number, val: string) => {
    updateShot(idx, { prompt: val.slice(0, 512) });
    const el = textareaRefs.current[idx];
    if (!el) return;
    const atIdx = imageInputs.length > 0 ? detectMentionAnchor(val, el.selectionStart ?? val.length) : null;
    if (atIdx !== null) {
      mentionAnchorRef.current = atIdx;
      setMentionShotIdx(idx);
      setMentionOpen(true);
      setMentionListIdx(0);
      return;
    }
    setMentionOpen(false);
  }, [imageInputs.length, updateShot]);

  const insertMention = useCallback((imgIdx: number) => {
    const anchor = mentionAnchorRef.current;
    const shotIdx = mentionShotIdx;
    if (anchor === null || shotIdx < 0) return;
    const el = textareaRefs.current[shotIdx];
    const current = shots[shotIdx]?.prompt ?? '';
    const cursorPos = el?.selectionStart ?? current.length;
    const img = imageInputs[imgIdx];
    if (!img) return;
    const prefix = anchor > 0 && !/\s/.test(current[anchor - 1] ?? '') ? ' ' : '';
    const label = `${prefix}@[${t('Image')}${img.idx}] `;
    const newPrompt = current.slice(0, anchor) + label + current.slice(cursorPos);
    updateShot(shotIdx, { prompt: newPrompt.slice(0, 512) });
    setMentionOpen(false);
    mentionAnchorRef.current = null;
    setTimeout(() => {
      if (el) {
        const pos = anchor + label.length;
        el.selectionStart = pos;
        el.selectionEnd = pos;
        el.focus();
      }
    }, 0);
  }, [mentionShotIdx, shots, imageInputs, t, updateShot]);

  const handleShotKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionOpen && imageInputs.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionListIdx(i => (i + 1) % imageInputs.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionListIdx(i => (i - 1 + imageInputs.length) % imageInputs.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionListIdx); return; }
      if (e.key === 'Escape') { e.preventDefault(); setMentionOpen(false); return; }
    }
    const el = e.currentTarget;
    const idx = Number(el.dataset.shotIdx);
    handleMentionAtomicKeys(e, (next) => updateShot(idx, { prompt: next }));
  }, [mentionOpen, imageInputs.length, mentionListIdx, insertMention, updateShot]);

  const removeShot = useCallback((index: number) => {
    setShots(prev => {
      if (prev.length <= MIN_SHOTS) return prev;
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      let pool = removed.duration;
      for (let i = next.length - 1; i >= 0 && pool > 0; i--) {
        const canTake = MAX_DUR - next[i].duration;
        if (canTake > 0) {
          const take = Math.min(canTake, pool);
          next[i] = { ...next[i], duration: next[i].duration + take };
          pool -= take;
        }
      }
      emit(next);
      return next;
    });
  }, [emit]);

  return (
    <div className="flex flex-col gap-2 w-full px-2.5 py-2.5" onFocus={onFocus} onBlur={onBlur}>
      {/* Header */}
      <div className="flex items-center gap-1 px-0.5 text-[10px] text-muted-foreground/50 flex-wrap">
        <span className="font-semibold">{t('Multi-Shot Mode')}</span>
        <span className="text-muted-foreground/30">·</span>
        <span className="tabular-nums">{t('{{current}} of {{max}} shots set', { current: shots.length, max: MAX_SHOTS })}</span>
        <span className="text-muted-foreground/30">·</span>
        <span className="tabular-nums">({t('total {{duration}}s', { duration: totalDuration })})</span>
      </div>

      {/* Shot list */}
      <div className="flex flex-col gap-1.5">
        {shots.map((shot, idx) => {
          const showMirror = hasMention(shot.prompt);
          return (
            <div key={idx} className="nodrag nowheel group relative flex flex-col gap-0.5 rounded-lg bg-muted/20 border border-border/15 p-1.5 transition-colors hover:border-border/30 focus-within:border-ring/40">
              {/* Shot header row */}
              <div className="flex items-center gap-1.5">
                {/* Remove button — always occupies space; hidden icon when at min */}
                <button
                  type="button"
                  onClick={() => removeShot(idx)}
                  className={`shrink-0 w-[18px] h-[18px] flex items-center justify-center rounded-sm transition-colors ${shots.length > MIN_SHOTS ? 'text-muted-foreground/30 hover:text-destructive/80' : 'text-transparent pointer-events-none'}`}
                  disabled={disabled || shots.length <= MIN_SHOTS}
                >
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path d="M2 2l4 4M6 2l-4 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
                {/* Shot label */}
                <span className="shrink-0 text-[10px] font-semibold text-muted-foreground/60 select-none whitespace-nowrap">
                  {t('Shot')} {idx + 1}
                </span>
                {/* Spacer */}
                <div className="flex-1" />
                {/* Duration control */}
                <div className="shrink-0 flex items-center gap-0 rounded-md bg-muted/50 h-[20px] select-none">
                  <button
                    type="button"
                    onClick={() => updateShot(idx, { duration: clamp(shot.duration - 1) })}
                    disabled={disabled || shot.duration <= MIN_DUR}
                    className="nodrag flex items-center justify-center w-[18px] h-full text-muted-foreground/40 hover:text-foreground/70 disabled:opacity-20 disabled:pointer-events-none transition-colors"
                  >
                    <svg width="6" height="6" viewBox="0 0 6 6" fill="none"><path d="M1 3h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
                  </button>
                  <div className="flex items-center gap-0.5 px-0.5">
                    <input
                      type="number"
                      min={MIN_DUR}
                      max={MAX_DUR}
                      value={shot.duration}
                      onChange={(e) => updateShot(idx, { duration: clamp(parseInt(e.target.value, 10) || 1) })}
                      className="nodrag nowheel w-[14px] h-[18px] bg-transparent text-center text-[10px] font-semibold text-foreground/80 outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      disabled={disabled}
                    />
                    <span className="text-[9px] text-muted-foreground/40">{t('sec')}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateShot(idx, { duration: clamp(shot.duration + 1) })}
                    disabled={disabled || shot.duration >= MAX_DUR}
                    className="nodrag flex items-center justify-center w-[18px] h-full text-muted-foreground/40 hover:text-foreground/70 disabled:opacity-20 disabled:pointer-events-none transition-colors"
                  >
                    <svg width="6" height="6" viewBox="0 0 6 6" fill="none"><path d="M3 1v4M1 3h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" /></svg>
                  </button>
                </div>
              </div>
              {/* Prompt textarea */}
              <div className="relative min-w-0">
                {showMirror && (
                  <div aria-hidden className={`pointer-events-none absolute inset-0 px-1.5 py-[2px] text-[11px] leading-[18px] whitespace-pre-wrap break-words overflow-hidden font-[inherit] [text-rendering:geometricPrecision] ${focusedIdx !== idx ? 'max-h-[40px]' : ''}`}>
                    {renderMentionMirror(shot.prompt, 'text-[11px]')}
                  </div>
                )}
                <textarea
                  ref={el => { textareaRefs.current[idx] = el; }}
                  data-shot-idx={idx}
                  value={shot.prompt}
                  onChange={(e) => handleShotPromptChange(idx, e.target.value)}
                  onKeyDown={handleShotKeyDown}
                  onClick={(e) => resolveMentionCaret(e.currentTarget, true)}
                  onKeyUp={(e) => resolveMentionCaret(e.currentTarget, false)}
                  placeholder={t('Enter prompt for this shot...')}
                  className={`nodrag nowheel w-full min-h-[22px] rounded-md bg-transparent px-1.5 py-[2px] text-[11px] leading-[18px] whitespace-pre-wrap break-words outline-none resize-none placeholder:text-muted-foreground/20 overflow-hidden font-[inherit] [text-rendering:geometricPrecision] ${focusedIdx !== idx ? 'max-h-[40px]' : ''} ${showMirror ? 'text-transparent caret-foreground' : 'text-foreground/90'}`}
                  rows={1}
                  disabled={disabled}
                  maxLength={512}
                  onFocus={(e) => {
                    setFocusedIdx(idx);
                    const el = e.currentTarget;
                    el.style.height = 'auto';
                    el.style.height = el.scrollHeight + 'px';
                  }}
                  onBlur={(e) => {
                    if ((e.relatedTarget as HTMLElement)?.closest?.('[data-mention-list]')) return;
                    setFocusedIdx(null);
                    setMentionOpen(false);
                    const el = e.currentTarget;
                    el.style.height = '';
                  }}
                  onInput={(e) => {
                    const el = e.currentTarget;
                    el.style.height = 'auto';
                    el.style.height = el.scrollHeight + 'px';
                  }}
                />
                {mentionOpen && mentionShotIdx === idx && imageInputs.length > 0 && (
                  <div data-mention-list className="absolute left-0 top-full z-50 mt-1 w-full rounded-xl border bg-popover shadow-2xl backdrop-blur-sm overflow-hidden">
                    <div className="p-1 max-h-[200px] overflow-y-auto custom-scrollbar">
                      {imageInputs.map((img, i) => (
                        <button
                          key={img.sourceId}
                          type="button"
                          className={`flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-xs text-left transition-colors ${i === mentionListIdx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/60'}`}
                          onMouseDown={(e) => { e.preventDefault(); insertMention(i); }}
                          onMouseEnter={() => setMentionListIdx(i)}
                        >
                          <Image src={img.url ?? ""} alt="" width={20} height={20} unoptimized className="w-5 h-5 rounded object-cover shrink-0 border border-border/30" />
                          <span className="truncate text-[11px] font-medium">{t('Image')}{img.idx}（{img.label}）</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Add shot button */}
      <button
        type="button"
        onClick={addShot}
        disabled={disabled || shots.length >= MAX_SHOTS}
        className="flex items-center justify-center gap-1 h-[26px] rounded-lg border border-dashed border-border/25 text-[10px] font-medium text-muted-foreground/30 hover:text-muted-foreground/60 hover:border-border/50 hover:bg-muted/20 transition-all disabled:opacity-25 disabled:pointer-events-none"
      >
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
          <path d="M4 1v6M1 4h6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        </svg>
        {t('Add Shot')}
      </button>
    </div>
  );
}
